import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Select, Space } from "antd";
import React, { FC, JSX, useCallback, useEffect, useState } from "react";
import { Service } from "../../entities/interfaces/service";
import ServiceService from "../../services/services.service";
import { Staff } from "../../entities/interfaces/staffs";
import StaffService from "../../services/staffs.service";
import { Selection } from "./ServiceSelector";
import { X } from "react-feather";
import { useStore } from "../../context/StoreContext";
import { DefaultOptionType } from "antd/es/select";

type Props = {
  onChange: (services: Selection[]) => void;
  disabled: boolean;
  member: boolean;
  onError: (error: string) => void;
  value: Selection[];
};

type IOption = {
  value: string;
  labelString: string;
  label: JSX.Element | string;
  price: number;
};

const ServiceSelectorV2: FC<Props> = ({ member, onChange, onError, value }) => {
  const { selectedStore } = useStore();
  const [form] = Form.useForm();
  const selections = Form.useWatch("selections", form);
  const [services, setServices] = useState<Service[]>([]);
  const [options, setOptions] = useState<IOption[]>([]);
  const [staffs, setStaffs] = useState<Staff[]>([]);

  useEffect(() => {
    fetchServices();
    fetchStaffs();
  }, []);

  useEffect(() => {
    form.setFieldValue("selections", value);
  }, [value]);

  useEffect(() => {
    handleSelection();
  }, [selections]);

  const handleSelection = async () => {
    if (selections) {
      onChange(selections);
      validateForm();
    }
  };

  const validateForm = () => {
    form
      .validateFields()
      .then()
      .catch((errorInfo) => {
        if (errorInfo.errorFields.length) {
          onError(errorInfo.errorFields[0].errors[0]);
        } else {
          onError("");
        }
      });
  };

  const fetchServices = async () => {
    const res = await ServiceService.list({});
    if (res.success) {
      if (res.data.length) {
        const availableServices = res.data.filter(
          (service) =>
            service.storeServices.length && service.storeServices[0]?.available
        );
        setServices(availableServices);
        updateOptions(availableServices);
      }
    }
  };

  const fetchStaffs = async () => {
    const res = await StaffService.listAll({});
    if (res?.success) {
      if (res.data.length) {
        setStaffs(res.data);
      }
    }
  };

  const updateOptions = (services: Service[]) => {
    setOptions(
      services.map((item) => ({
        value: item.id,
        labelString: item.name,
        price: item.storeServices[0]?.price,
        label: (
          <p>
            {/* TODO: update price */}
            <span className="mr-2">{item.name}</span>
            <span className={`${member ? "line-through" : ""}`}>
              ₹{item.storeServices[0].price}
            </span>
            {member && (
              <span className="ml-3 text-green-600">
                ₹{item.storeServices[0].memberPrice}
              </span>
            )}
          </p>
        ),
      }))
    );
  };

  let timeout: ReturnType<typeof setTimeout> | null;
  let currentValue: string;
  const fetchData = (value: string) => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    currentValue = value;

    const fetcher = async () => {
      const filteredServices = services.filter((service) =>
        service.name.toLowerCase().includes(currentValue.toLowerCase())
      );
      updateOptions(filteredServices);
    };
    if (value) {
      timeout = setTimeout(fetcher, 500);
    }
  };

  const addNewSelection = (id: string, service: Service) => {
    const currentList = (selections || []) as Selection[];
    form.setFieldsValue({
      selections: [
        ...currentList,
        { ...service, qty: 1, additionalCharges: undefined, staffId: "" },
      ],
    });
  };

  const handleChange = (id: string) => {
    const selectedService = services.find((srv) => srv.id === id);

    if (!selectedService) return;
    addNewSelection(id, selectedService);
    form.setFieldValue("serviceSelector", "");
  };

  const renderStaffOptions = () => {
    const { storeStaffs, otherStaffs } = staffs.reduce<{
      storeStaffs: DefaultOptionType[];
      otherStaffs: DefaultOptionType[];
    }>(
      (acc, curr) => {
        if (curr.storeId === selectedStore?.id) {
          acc.storeStaffs.push({
            label: <span>{curr.name}</span>,
            value: curr.id,
          });
        } else {
          acc.otherStaffs.push({
            label: <span>{curr.name}</span>,
            value: curr.id,
          });
        }
        return acc;
      },
      { storeStaffs: [], otherStaffs: [] }
    );

    return [
      {
        label: <span>Store Staffs</span>,
        title: "Store Staffs",
        options: storeStaffs,
      },
      {
        label: <span>Other Staffs</span>,
        title: "Other Staffs",
        options: otherStaffs,
      },
    ];
  };

  return (
    <Form
      name="dynamic_form_nest_item"
      autoComplete="off"
      form={form}
      initialValues={{
        selections: [],
      }}
    >
      {/* <Select
        className="w-full mb-4"
        showSearch
        placeholder={"Search Services"}
        defaultActiveFirstOption={false}
        suffixIcon={null}
        allowClear
        filterOption={false}
        value={""}
        onSearch={fetchData}
        onChange={handleChange}
        notFoundContent={null}
        options={options}
        // disabled={disabled}
        size="large"
      /> */}
      <Form.Item name="serviceSelector">
        <Select
          showSearch
          placeholder={"Search Services"}
          className="w-full"
          size="large"
          onChange={handleChange}
          filterOption={(input, option) =>
            (option?.labelString ?? "")
              .toLowerCase()
              .includes(input.toLowerCase()) ||
            `${option?.price ?? ""}`.toLowerCase().includes(input.toLowerCase())
          }
          options={options}
        />
      </Form.Item>
      <div
        className={`${
          selections?.length > 0 && "bg-gray-100 p-2"
        } rounded-md gap-1  flex flex-col`}
      >
        <Form.List name="selections">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name: idx, ...restField }) => {
                const { storeServices = [], name }: Selection =
                  selections[idx] || {};
                const service = storeServices?.[0];
                if (!service) return;
                const { memberPrice, price } = service;
                return (
                  <div className="p-3 border rounded-md bg-white  relative">
                    <div className="flex justify-between items-start mb-3">
                      <p className="mr-6">
                        {/* TODO: update price */}
                        <span className="mr-2">{name}</span>
                        <span
                          className={`${
                            member ? "line-through" : ""
                          } font-semibold`}
                        >
                          ₹{price}
                        </span>
                        {member && (
                          <span className="ml-3 text-green-600 font-semibold">
                            ₹{memberPrice}
                          </span>
                        )}
                      </p>
                      <button
                        className="bg-primary w-5 h-5 rounded-full flex items-center justify-center border mt-1.5"
                        onClick={() => remove(idx)}
                      >
                        <X
                          size={12}
                          className="text-xs font-semibold"
                          color="white"
                        />
                      </button>
                    </div>
                    <div className="flex items-start  gap-4">
                      <Form.Item
                        className="m-0 w-[150px]"
                        name={[idx, "staffId"]}
                        rules={[
                          { required: true, message: "Please select staff!" },
                        ]}
                      >
                        <Select
                          placeholder="Select Staff"
                          options={renderStaffOptions()}
                          onSelect={validateForm}
                          prefix="Staff"
                        />
                      </Form.Item>
                      <Form.Item
                        className="m-0"
                        name={[idx, "additionalCharges"]}
                      >
                        <InputNumber
                          placeholder="Extra"
                          min={0}
                          addonBefore={"Extras"}
                          className="w-36"
                        />
                      </Form.Item>
                      <Form.Item className="m-0" name={[idx, "qty"]}>
                        <InputNumber
                          addonBefore={"Quantity"}
                          className="w-36"
                        />
                      </Form.Item>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </Form.List>
      </div>
    </Form>
  );
};

export default ServiceSelectorV2;
